/**
 * pickStrategyColor — deterministic hex color from a strategy id.
 *
 * Used by StrategyLegend to color-code publications when multiple
 * strategies overlay on the cross-plan calendar view. The palette has 12
 * perceptually-distinct hues; same id → same color on every render and
 * across reloads. Persisted to `strategy.displayColor` on first read so
 * the color never drifts if the palette ever changes.
 */

/**
 * 12-color palette tuned for readability on both light and dark themes.
 * Derived from Tailwind-style hues at similar chroma so no single swatch
 * dominates. Keep in sync with `STRATEGY_COLORS` below when changing.
 */
export const STRATEGY_COLORS: readonly string[] = [
  '#2563EB', // blue-600
  '#DC2626', // red-600
  '#16A34A', // green-600
  '#CA8A04', // yellow-600
  '#9333EA', // purple-600
  '#0891B2', // cyan-600
  '#EA580C', // orange-600
  '#DB2777', // pink-600
  '#4F46E5', // indigo-600
  '#059669', // emerald-600
  '#7C3AED', // violet-600
  '#D97706', // amber-600
] as const;

/**
 * FNV-1a 32-bit hash — compact, no deps, stable across platforms.
 * Matches the hashing style in assignTimeSlots so seeding is consistent
 * across content-plan utilities.
 */
function hashString(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h * 16777619) >>> 0;
  }
  return h;
}

/** Deterministic color for an arbitrary strategy id. */
export function pickStrategyColor(strategyId: string): string {
  const idx = hashString(strategyId) % STRATEGY_COLORS.length;
  return STRATEGY_COLORS[idx];
}

/**
 * Assign distinct colors to a list of strategies. When a strategy already
 * has `displayColor` stored, it is preserved even if it would collide
 * with a later unassigned strategy; unassigned strategies then pick the
 * first unclaimed palette slot in a deterministic order.
 *
 * Guarantees no two strategies in the input share a color as long as
 * `strategies.length <= STRATEGY_COLORS.length`. Beyond that, reuse is
 * unavoidable and the deterministic `pickStrategyColor` fallback kicks
 * in (legend shows strategy name next to swatch to stay readable).
 */
export function assignStrategyColors(
  strategies: Array<{ id: string; displayColor?: string }>,
): Record<string, string> {
  const result: Record<string, string> = {};
  const used = new Set<string>();

  for (const s of strategies) {
    if (s.displayColor) {
      result[s.id] = s.displayColor;
      used.add(s.displayColor);
    }
  }

  for (const s of strategies) {
    if (result[s.id]) continue;

    const preferred = pickStrategyColor(s.id);
    if (!used.has(preferred)) {
      result[s.id] = preferred;
      used.add(preferred);
      continue;
    }

    // Collision — walk the palette deterministically from the preferred
    // index until we find an unclaimed slot.
    const startIdx = STRATEGY_COLORS.indexOf(preferred);
    let chosen = preferred;
    for (let step = 1; step <= STRATEGY_COLORS.length; step++) {
      const idx = (startIdx + step) % STRATEGY_COLORS.length;
      if (!used.has(STRATEGY_COLORS[idx])) {
        chosen = STRATEGY_COLORS[idx];
        break;
      }
    }
    result[s.id] = chosen;
    used.add(chosen);
  }

  return result;
}
